import { CaretRightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button } from 'antd';
import EditorPreviewer from 'components/EditorPreviewer';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useMustacheSticker2Url from 'hooks/business/useMustacheSticker2Url';
import dayjs from 'lib/dayjs';
import { HTMLAttributes, ReactNode, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { regionLanguageMap, regionList } from 'services/config/region';
import sort from 'utils/sort';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import { getSlateExecuteContext } from 'views/Aide/Scenario/PolicyForm';
import {
  getResultContexts,
  ResultText,
} from 'views/Aide/Scenario/PolicyForm/BilingualExecuteContextField';

export function useReplyOptionLabel() {
  const { t } = useTranslate();

  const replyOptionLabel = {
    DEFAULT: t('aide.买家咨询结束'),
    '1': t('aide.买家给出好评'),
    '2': t('aide.买家给出差评'),
  };

  return {
    t,
    replyOptionLabel,
  };
}

export interface ReplyPreviewProps extends HTMLAttributes<HTMLDivElement> {
  showHeader?: boolean;
  preSaleDelayMin?: number;
  afterSaleDelayMin?: number;
  policy: Pick<Marketing.Policy, 'questionnaireExecuteContext'>;
  platform: General.PlatformUpperCase;
  onModifyReply?: (replyOption: Marketing.ReplyOption) => void;
}

function ReplyPreview(props: ReplyPreviewProps) {
  const {
    showHeader = true,
    preSaleDelayMin = 5,
    afterSaleDelayMin = 10,
    policy,
    platform,
    onModifyReply,
    ...otherProps
  } = props;

  const systemLanguage = useSystemLanguageValue();

  const { region, replyLanguage, reply } = useMemo(() => {
    const { questionnaireExecuteContext: { questionnaireRegionExecuteContexts = [] } = {} } =
      policy;

    const upperCaseRegionList = regionList.map(toUpperCase);

    const sortedList = sort([...questionnaireRegionExecuteContexts], 'region', upperCaseRegionList);

    if (sortedList.length === 0) {
      return {
        region: upperCaseRegionList[0],
        replyLanguage: systemLanguage,
        reply: {},
      };
    }

    const [{ region, replyOptionExecuteContexts = [] }] = sortedList;

    const { [toLowerCase(region)]: replyLanguage } = regionLanguageMap;

    const reply = replyOptionExecuteContexts.reduce<
      Partial<Record<Marketing.ReplyOption, Marketing.ExecuteContext>>
    >((res, { replyOption, executeContext }) => {
      res[replyOption] = executeContext;

      return res;
    }, {});

    return {
      region,
      replyLanguage,
      reply,
    };
  }, [policy, systemLanguage]);

  const { t, replyOptionLabel } = useReplyOptionLabel();

  const [getStickerUrl] = useMustacheSticker2Url(platform, region);

  const extraRender = (replyOption: Marketing.ReplyOption) => {
    return onModifyReply ? (
      <Button type="primary" onClick={() => onModifyReply(replyOption)}>
        {`${t('ViewsChat.调整回复内容')} >`}
      </Button>
    ) : undefined;
  };

  type Message = {
    type: 'system' | 'query' | 'reply';
    content: Partial<Record<General.Language, ReactNode>>;
  };

  const messageRender = (message: Message, index: number) => {
    const { content } = message;

    const {
      'zh-CN': cnContent,
      'en-US': enContent = cnContent,
      [systemLanguage]: systemContent = enContent,
      [replyLanguage]: replyContent,
    } = content;

    if (message.type === 'system') {
      return (
        <SystemMessage key={index}>
          <div>{systemContent}</div>
        </SystemMessage>
      );
    }

    const { type } = message;

    const Component = {
      query: QueryMessage,
      reply: ReplyMessage,
    }[type];

    return (
      <Component key={index}>
        <div>
          {type === 'query' && t('marketing.买家昵称')}
          <span>
            {dayjs()
              .subtract(1, 'days')
              .hour(12)
              .minute(31 + index)
              .format('YYYY-MM-DD HH:mm')}
          </span>
          {type === 'reply' && t('refundSetting.客服昵称')}
        </div>
        {typeof systemContent === 'string' ? (
          <section>
            {systemContent !== replyContent && replyContent && <p>{replyContent}</p>}
            <p>{systemContent}</p>
          </section>
        ) : (
          systemContent
        )}
      </Component>
    );
  };

  const exampleRender = (replyOption: Marketing.ReplyOption, messages: Message[]) => {
    const replyMessages = ((): Message[] => {
      const { [replyOption]: executeContext } = reply;

      if (!executeContext) {
        return [];
      }

      const contexts = getResultContexts(getSlateExecuteContext(executeContext)) || [];

      if (contexts.length === 0) {
        return [];
      }

      return contexts.map((context) => {
        return {
          type: 'reply',
          content: {
            'zh-CN': (
              <ResultText
                style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  fontSize: '14px',
                  borderRadius: '4px',
                  background: '#DFFDE9',
                }}
              >
                <EditorPreviewer getStickerUrl={getStickerUrl} value={context} />
              </ResultText>
            ),
          },
        };
      });
    })();

    return (
      <Example>
        <section>
          <header>
            <h4>{replyOptionLabel[replyOption]}</h4>
          </header>
          <div>{[...messages, ...replyMessages].map(messageRender)}</div>
        </section>
        <div>{extraRender(replyOption)}</div>
      </Example>
    );
  };

  return (
    <Container {...otherProps}>
      {showHeader && (
        <header>
          <h3>{t('aide.策略分支')}</h3>
          <span>
            {preSaleDelayMin === afterSaleDelayMin
              ? t(
                  'aide.当会话中最后一条买家消息已被有效回复后 x 分钟 买家仍没有发送新消息时，自动发送评分邀请',
                  { x: preSaleDelayMin },
                )
              : t(
                  'aide.当会话中最后一条买家消息已被有效回复后 售前买家x分钟 / 售后买家y分钟 买家仍没有发送新消息时，自动发送评分邀请',
                  { x: preSaleDelayMin, y: afterSaleDelayMin },
                )}
          </span>
        </header>
      )}
      <div>
        <Left>
          {exampleRender('DEFAULT', [
            {
              type: 'query',
              content: {
                'zh-CN': '什么时候发货？',
                'en-US': 'When will it be shipped?',
                id: 'Kapan paket akan dikirimkan?',
                tl: 'Kailan mashiship?',
                vi: 'Bao giờ gửi hàng？',
                th: 'การจัดส่งสินค้าเมื่อไหร่',
                pt: 'Quando será enviado o pedido?',
                es: 'When will it be shipped?',
              },
            },
            {
              type: 'reply',
              content: {
                'zh-CN': '您下单后，1-2天处理发货哦',
                'en-US': 'It will be shipped within 1-2 days after you place an order.',
                id: 'Setelah kakak checkout, paket akan dikirim dalam waktu 1-2 hari kak',
                tl: 'Ishiship ito sa loob ng 1-2 days after ma-place ang order.',
                vi: 'Khoảng 1-2 ngày sau khi bạn lên đơn ạ.',
                th: 'หลังจากคุณสั่งรายการแล้ว 1-2 วัน จัดส่งสินค้า',
                pt: 'Depois de fazer o pedido,  será enviado dentro de 1-2 dias',
                es: 'It will be shipped within 1-2 days after you place an order.',
              },
            },
            {
              type: 'query',
              content: {
                'zh-CN': '好的谢谢',
                'en-US': 'Ok, thanks.',
                id: 'Baik kak, terima kasih',
                tl: 'Okay, thanks.',
                vi: 'Cảm ơn bạn.',
                th: 'เอเคค่ะ ขอบคุณค่ะ',
                pt: 'Ok，obrigado',
                es: 'Ok, thanks.',
              },
            },
            {
              type: 'reply',
              content: {
                'zh-CN': '不客气',
                'en-US': `You're welcome.`,
                id: 'Sama-sama ^^',
                tl: `You're welcome.`,
                vi: 'Không có gì!',
                th: 'ด้วยความยินดี',
                pt: 'De nada',
                es: `You're welcome.`,
              },
            },
            {
              type: 'system',
              content: {
                'zh-CN': t('aide.买家咨询结束后，自动发送接待评分邀请'),
              },
            },
          ])}
        </Left>
        <Right>
          <RightTop>
            <span />
            <div>
              <CaretRightOutlined />
              <div />
            </div>
            {exampleRender('1', [
              {
                type: 'query',
                content: {
                  'zh-CN': '1',
                },
              },
            ])}
          </RightTop>
          <RightBottom>
            <span />
            <div>
              <CaretRightOutlined />
              <div />
            </div>
            {exampleRender('2', [
              {
                type: 'query',
                content: {
                  'zh-CN': '2',
                },
              },
            ])}
          </RightBottom>
        </Right>
      </div>
    </Container>
  );
}

const Container = styled.div`
  border-radius: 2px;
  > header {
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 24px;
    margin-bottom: 1px;
    background: #fff;
    > h3 {
      flex: 0 0 auto;
      margin-bottom: 0;
    }
    > span {
      margin-left: 8px;
    }
  }
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px 24px;
    background: #fff;
  }
`;

const lineWidth = 36;
const gapWidth = 4;
const margin = 7;
const color = '#A9BCFF';

const Left = styled.div`
  width: calc(50% - ${lineWidth + gapWidth}px);
  max-width: calc(800px - ${lineWidth + gapWidth}px);
`;

const Right = styled.div`
  width: calc(50% + ${lineWidth + gapWidth}px);
  max-width: calc(800px + ${lineWidth + gapWidth}px);
  > * {
    display: flex;
    > span {
      flex: 0 0 ${lineWidth}px;
      margin-left: ${gapWidth}px;
    }
    > div {
      flex: 0 0 ${lineWidth}px;
      align-self: stretch;
      display: flex;
      position: relative;
      margin-right: ${gapWidth}px;
      > span {
        position: absolute;
        right: -${gapWidth}px;
        transform: scaleX(1.5);
        color: ${color};
      }
    }
    > div > div {
      flex: 1 0 0;
      height: 50%;
      border-left: solid 2px ${color};
    }
    > section {
      flex: 1 0 0;
    }
  }
`;

const RightTop = styled.div`
  margin-bottom: ${margin * 2}px;
  > span {
    align-self: flex-end;
    margin-bottom: -${margin}px;
    border-bottom: solid 1px ${color};
  }
  > div {
    align-items: flex-end;
    margin-bottom: -${margin}px;
    > span {
      top: calc(50% - ${margin - 1}px);
    }
  }
  > div > div {
    border-top: solid 2px ${color};
  }
`;

const RightBottom = styled.div`
  > span {
    align-self: flex-start;
    margin-top: -${margin}px;
    border-top: solid 1px ${color};
  }
  > div {
    align-items: flex-start;
    margin-top: -${margin}px;
    > span {
      bottom: calc(50% - ${margin - 1}px);
    }
  }
  > div > div {
    border-bottom: solid 2px ${color};
  }
`;

const Example = styled.section`
  > section {
    border: solid 1px #f0f2f5;
    border-radius: 2px;
    background: #f7f9fa;
    > header {
      display: flex;
      align-items: center;
      height: 48px;
      padding: 0 16px;
      background: #fff;
      > h4 {
        position: relative;
        margin-bottom: 0;
        font-size: 16px;
        text-indent: 12px;
        &::before {
          content: '';
          display: inline-block;
          position: absolute;
          top: 4px;
          left: 0;
          width: 4px;
          height: 16px;
          background: var(--ant-primary-color);
        }
      }
    }
    > div {
      padding: 12px 16px;
      > * + * {
        margin-top: 12px;
      }
    }
  }
  > div {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  > div {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    font-size: 12px;
    color: #515359;
    > span {
      margin: 0 6px;
      color: #b5b9bf;
    }
  }
  > section {
    max-width: 85%;
    padding: 10px 14px;
    border-radius: 4px;
    > p {
      margin-bottom: 0;
      white-space: pre-wrap;
      & + p {
        margin-top: 8px;
        padding-top: 8px;
        color: #84868c;
        border-top: solid 1px #ececec;
      }
    }
  }
  > button {
    margin-top: 8px;
  }
`;

const ReplyMessage = styled(Message)`
  align-items: flex-end;
  > section {
    background: #dfe9fc;
  }
`;

const QueryMessage = styled(Message)`
  align-items: flex-start;
  > section {
    background: #fff;
  }
`;

const SystemMessage = styled.div`
  display: flex;
  justify-content: center;
  margin: 18px 0 !important;
  font-size: 12px;
  > div {
    max-width: 80%;
    padding: 4px 14px;
    line-height: 20px;
    text-align: center;
    background: #f0f2f5;
    border-radius: 50px;
  }
`;

export default ReplyPreview;
